import { useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ArrowUpRight } from "lucide-react";

const drawerVariants = {
  hidden: { x: "100%", opacity: 0 },
  visible: {
    x: 0,
    opacity: 1,
    transition: { duration: 0.65, ease: [0.16, 1, 0.3, 1] },
  },
  exit: {
    x: "100%",
    opacity: 0,
    transition: { duration: 0.4, ease: [0.4, 0, 1, 1] },
  },
};

const backdropVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: 0.3 } },
  exit: { opacity: 0, transition: { duration: 0.3 } },
};

const ShowcaseDrawer = ({ project, onClose }) => {
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, []);

  useEffect(() => {
    const handleKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [onClose]);

  if (!project) return null;

  const { accent } = project;

  return (
    <AnimatePresence>
      {/* Backdrop */}
      <motion.div
        key="sc-backdrop"
        variants={backdropVariants}
        initial="hidden"
        animate="visible"
        exit="exit"
        style={{
          position: "fixed", inset: 0, zIndex: 90,
          background: "rgba(0,0,0,0.78)",
          backdropFilter: "blur(6px)",
          WebkitBackdropFilter: "blur(6px)",
        }}
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Drawer */}
      <motion.div
        key="sc-drawer"
        variants={drawerVariants}
        initial="hidden"
        animate="visible"
        exit="exit"
        role="dialog"
        aria-modal="true"
        aria-label={`Showcase: ${project.title}`}
        style={{
          position: "fixed", right: 0, top: 0,
          zIndex: 100,
          height: "100dvh",
          width: "min(520px, 95vw)",
          display: "flex",
          flexDirection: "column",
          background: "#09090B",
          borderLeft: "1px solid rgba(255,255,255,0.07)",
          overflow: "hidden",
        }}
      >
        {/* Top accent line */}
        <div style={{
          height: "2px", flexShrink: 0,
          background: `linear-gradient(90deg, transparent 0%, ${accent} 40%, ${accent} 60%, transparent 100%)`,
        }} />

        {/* Top bar */}
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          padding: "14px 24px", flexShrink: 0,
          borderBottom: "1px solid rgba(255,255,255,0.05)",
        }}>
          <p style={{
            fontFamily: "var(--font-robert-medium)", fontSize: "8px",
            letterSpacing: "0.32em", color: "rgba(255,255,255,0.25)",
            textTransform: "uppercase",
          }}>
            Creative Experiment
          </p>
          <button
            onClick={onClose}
            aria-label="Close showcase"
            style={{
              display: "flex", alignItems: "center", justifyContent: "center",
              width: 30, height: 30, borderRadius: "6px",
              background: "rgba(255,255,255,0.04)",
              border: "1px solid rgba(255,255,255,0.08)",
              color: "rgba(255,255,255,0.35)", cursor: "pointer",
              transition: "background 0.2s, color 0.2s",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = "rgba(255,255,255,0.1)";
              e.currentTarget.style.color = "#ffffff";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "rgba(255,255,255,0.04)";
              e.currentTarget.style.color = "rgba(255,255,255,0.35)";
            }}
          >
            <X size={13} />
          </button>
        </div>

        {/* Scrollable content */}
        <div style={{ flex: 1, overflowY: "auto", scrollbarWidth: "none" }}>

          {/* Hero image */}
          <div style={{ position: "relative", flexShrink: 0 }}>
            <img
              src={project.previewImg}
              alt={`${project.title} preview`}
              draggable={false}
              style={{
                width: "100%",
                height: "clamp(170px,26vh,260px)",
                objectFit: "cover",
                display: "block",
              }}
            />
            {/* gradient fade into drawer bg */}
            <div style={{
              position: "absolute", inset: 0,
              background: "linear-gradient(to top, #09090B 0%, rgba(9,9,11,0.55) 45%, rgba(9,9,11,0.05) 100%)",
            }} />
            {/* Corner accent orb */}
            <div style={{
              position: "absolute", top: "-20px", right: "-20px",
              width: "140px", height: "140px", borderRadius: "50%",
              background: `radial-gradient(circle, ${accent}30 0%, transparent 70%)`,
              filter: "blur(28px)", pointerEvents: "none",
            }} />
          </div>

          {/* Body */}
          <div style={{ padding: "0 clamp(16px,3.5vw,32px) clamp(24px,4vw,48px)" }}>

            {/* Type tag + title */}
            <div style={{ marginBottom: "16px" }}>
              <p style={{
                fontFamily: "var(--font-robert-medium)", fontSize: "8px",
                letterSpacing: "0.28em", textTransform: "uppercase",
                color: accent, marginBottom: "8px",
              }}>
                {project.type}
              </p>
              <h2 style={{
                fontFamily: "var(--font-robert-medium)", fontWeight: 700,
                fontSize: "clamp(1.9rem,4.5vw,3rem)",
                lineHeight: 1.02, color: "#F5F5F7",
                letterSpacing: "-0.025em",
              }}>
                {project.title}
              </h2>
            </div>

            {/* CTAs — right below the title */}
            <div style={{ display: "flex", gap: "10px", flexWrap: "wrap", marginBottom: "20px" }}>
              <a
                href={project.liveLink}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: "inline-flex", alignItems: "center", gap: "6px",
                  padding: "9px 20px", borderRadius: "9999px",
                  background: accent, color: "#09090B",
                  fontFamily: "var(--font-robert-medium)",
                  fontSize: "clamp(0.58rem,0.78vw,0.66rem)",
                  fontWeight: 700, letterSpacing: "0.12em",
                  textDecoration: "none", textTransform: "uppercase",
                  boxShadow: `0 4px 18px ${accent}44`,
                }}
              >
                Live Demo <ArrowUpRight size={11} />
              </a>
              {project.codeLink && (
                <a
                  href={project.codeLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    display: "inline-flex", alignItems: "center", gap: "6px",
                    padding: "9px 20px", borderRadius: "9999px",
                    background: "rgba(255,255,255,0.05)",
                    border: "1px solid rgba(255,255,255,0.1)",
                    color: "rgba(255,255,255,0.55)",
                    fontFamily: "var(--font-robert-medium)",
                    fontSize: "clamp(0.58rem,0.78vw,0.66rem)",
                    fontWeight: 600, letterSpacing: "0.12em",
                    textDecoration: "none", textTransform: "uppercase",
                    transition: "background 0.2s, color 0.2s",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = "rgba(255,255,255,0.1)";
                    e.currentTarget.style.color = "rgba(255,255,255,0.85)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = "rgba(255,255,255,0.05)";
                    e.currentTarget.style.color = "rgba(255,255,255,0.55)";
                  }}
                >
                  View Code <ArrowUpRight size={11} />
                </a>
              )}
            </div>

            {/* Thin rule */}
            <div style={{
              height: "1px", marginBottom: "20px",
              background: `linear-gradient(90deg, ${accent}40, rgba(255,255,255,0.04) 60%, transparent)`,
            }} />

            {/* Description */}
            <p style={{
              fontFamily: "var(--font-robert-regular)",
              fontSize: "clamp(0.82rem,1.1vw,0.94rem)",
              color: "rgba(255,255,255,0.45)", lineHeight: 1.72,
              marginBottom: "28px",
            }}>
              {project.description}
            </p>

            {/* What it demonstrates */}
            <div style={{ marginBottom: "28px" }}>
              <p style={{
                fontFamily: "var(--font-robert-medium)", fontSize: "8px",
                letterSpacing: "0.3em", textTransform: "uppercase",
                color: "rgba(255,255,255,0.22)", marginBottom: "14px",
              }}>
                What It Demonstrates
              </p>
              <div style={{ display: "flex", flexDirection: "column", gap: "11px" }}>
                {project.highlights.map((h, i) => (
                  <div key={i} style={{ display: "flex", gap: "14px", alignItems: "flex-start" }}>
                    <span style={{
                      fontFamily: "var(--font-robert-medium)", fontSize: "9px",
                      color: accent, flexShrink: 0, marginTop: "4px",
                      letterSpacing: "0.06em", fontWeight: 700,
                    }}>
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <p style={{
                      fontFamily: "var(--font-robert-medium)",
                      fontSize: "clamp(0.76rem,1vw,0.86rem)",
                      color: "rgba(255,255,255,0.58)", lineHeight: 1.6,
                    }}>
                      {h}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            {/* Stack */}
            <div style={{ marginBottom: "32px" }}>
              <p style={{
                fontFamily: "var(--font-robert-medium)", fontSize: "8px",
                letterSpacing: "0.3em", textTransform: "uppercase",
                color: "rgba(255,255,255,0.22)", marginBottom: "12px",
              }}>
                Stack
              </p>
              <div style={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
                {project.stack.map((tech) => (
                  <span
                    key={tech}
                    style={{
                      fontFamily: "var(--font-robert-medium)",
                      fontSize: "clamp(0.6rem,0.8vw,0.7rem)",
                      fontWeight: 600, letterSpacing: "0.04em",
                      padding: "5px 13px", borderRadius: "9999px",
                      background: `${accent}14`,
                      border: `1px solid ${accent}32`,
                      color: accent,
                    }}
                  >
                    {tech}
                  </span>
                ))}
              </div>
            </div>

          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default ShowcaseDrawer;
